import React, { useState, useContext, useEffect } from 'react';
import { TIMEZONE_LIST } from '../../utils/timezones';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePreferences } from '../../hooks/usePreferences';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Configure Day.js plugins
dayjs.extend(utc);
dayjs.extend(timezone);
import axios from 'axios';
import { API_BASE_URL, getAuthHeader } from '../../utils/api';
import { useAuctionHouses } from './hooks/useAuctionHouses';
import { SystemAuctionLots } from './SystemAuctionLots';
import BaseTable from '../../components/shared/table/BaseTable';
import { TableProvider, TableContext } from '../../components/shared/table/context/TableContext';
import { SystemAuctionsToolbar } from './SystemAuctionsToolbar';

// Content component that uses TableContext
function SystemAuctionsContent({ columns, data, onAdd, loading, error }) {
  const { setColumns } = useContext(TableContext);

  useEffect(() => {
    setColumns(columns);
  }, [columns, setColumns]);

  return (
    <>
      <SystemAuctionsToolbar
        columns={columns}
        data={data}
        onAdd={onAdd}
      />
      <BaseTable
        data={data}
        columns={columns}
        loading={loading}
        error={error}
      />
    </>
  );
}

// Configure axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

// Add auth header interceptor
api.interceptors.request.use(async (config) => {
  const headers = await getAuthHeader(config.method);
  config.headers = { ...config.headers, ...Object.fromEntries(headers.entries()) };
  return config;
});

// Form for creating/editing system auctions
function SystemAuctionForm({ auction, onSubmit, onCancel }) {
  const [formData, setFormData] = useState(() => {
    if (auction) {
      // Format existing auction dates and interval properly
      const formattedAuction = {
        ...auction,
        date: new Date(auction.date).toISOString().split('T')[0],
        end_date: auction.end_date ? new Date(auction.end_date).toISOString().split('T')[0] : '',
        daily_times: Object.fromEntries(
          Object.entries(auction.daily_times || {}).map(([date, time]) => [
            new Date(date).toISOString().split('T')[0],
            time || ''
          ])
        )
      };

      // Format scraping_frequency to match select options
      if (typeof auction.scraping_frequency === 'string') {
        try {
          const [amount, unit] = auction.scraping_frequency.split(' ');
          if (amount && unit) {
            formattedAuction.scraping_frequency = `${amount} ${unit}`;
          } else {
            formattedAuction.scraping_frequency = '1 days'; // Default if format is invalid
          }
        } catch (e) {
          formattedAuction.scraping_frequency = '1 days'; // Default if split fails
        }
      } else {
        formattedAuction.scraping_frequency = '1 days'; // Default if not a string
      }

      return formattedAuction;
    }
    return {
      name: '',
      date: new Date().toISOString(),
      end_date: '',
      auction_house_id: '',
      currency: 'USD',
      buyers_premium_percent: 20.00,
      buyers_premium_minimum: 0.00,
      source_url: '',
      scraping_enabled: true,
      scraping_frequency: '1 days',
      daily_times: {},
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      status: 'pending'
    };
  });

  const { data: auctionHouses, isLoading: auctionHousesLoading } = useAuctionHouses();
  const [error, setError] = useState(null);

  // Generate array of dates between start and end date
  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date(startDate);
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      dates.push(date.toISOString().split('T')[0]); // Return YYYY-MM-DD format
    }
    
    return dates;
  };

  // Get dates array based on current form data
  const dateRange = getDatesInRange(formData.date, formData.end_date);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.date || !formData.auction_house_id) {
      setError('Please fill in all required fields');
      return;
    }

    // Ensure we have a time for the start date
    if (!formData.daily_times[formData.date]) {
      setError('Start time is required');
      return;
    }

    // Clean up the data before submission
    const auctionData = {
      ...formData,
      // Remove end_date if it's empty
      end_date: formData.end_date || null,
      daily_times: {
        [formData.date]: formData.daily_times[formData.date],
        ...(formData.end_date ? Object.fromEntries(
          Object.entries(formData.daily_times)
            .filter(([date, time]) => date !== formData.date && time)
        ) : {})
      }
    };

    onSubmit(auctionData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="text-red-600 text-sm">{error}</div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Start Date *
          </label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => {
              const newStartDate = e.target.value;
              setFormData(prev => ({
                ...prev,
                date: newStartDate,
                // Clear end date if it's before new start date
                end_date: prev.end_date && new Date(prev.end_date) < new Date(newStartDate).toISOString() ? '' : prev.end_date,
                // Reset times for dates no longer in range
                daily_times: {}
              }));
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            End Date
          </label>
          <input
            type="date"
            value={formData.end_date}
            min={formData.date}
            onChange={(e) => {
              setFormData(prev => ({
                ...prev,
                end_date: e.target.value,
                // Reset times for dates no longer in range
                daily_times: {}
              }));
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
          <p className="mt-1 text-sm text-gray-500">
            Optional. Leave empty for single-day auctions.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Auction House *
        </label>
        <select
          value={formData.auction_house_id}
          onChange={e => {
            const selectedHouseId = parseInt(e.target.value) || '';
            const selectedHouse = auctionHouses?.find(house => house.id === selectedHouseId);
            setFormData(prev => ({ 
              ...prev, 
              auction_house_id: selectedHouseId,
              buyers_premium_percent: selectedHouse?.default_buyers_premium_percent || 20.00,
              buyers_premium_minimum: selectedHouse?.default_buyers_premium_minimum || 0.00,
              currency: selectedHouse?.default_currency || 'USD',
              timezone: selectedHouse?.default_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
            }));
          }}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        >
          <option value="">Select an auction house</option>
          {auctionHouses?.map(house => (
            <option key={house.id} value={house.id}>
              {house.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Timezone *
        </label>
          <select
            value={formData.timezone}
            onChange={e => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          >
            {TIMEZONE_LIST.map(tz => (
              <option key={tz} value={tz}>
                {tz} ({new Date().toLocaleTimeString('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit' })})
              </option>
            ))}
          </select>
        <p className="mt-1 text-sm text-gray-500">
          Defaults to auction house's timezone
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Auction Times *
        </label>
        <div className="grid grid-cols-2 gap-4">
          {dateRange.map(date => (
            <div key={date} className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 w-32">
                {date.split('-').slice(1).concat(date.split('-')[0]).join('/')}
              </span>
              <input
                type="time"
                required
                value={formData.daily_times[date] ?? ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  daily_times: {
                    ...prev.daily_times,
                    [date]: e.target.value
                  }
                }))}
                className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Buyer's Premium % *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={formData.buyers_premium_percent}
            onChange={e => setFormData(prev => ({ 
              ...prev, 
              buyers_premium_percent: parseFloat(e.target.value) || 0 
            }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Buyer's Premium Minimum *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.buyers_premium_minimum}
            onChange={e => setFormData(prev => ({ 
              ...prev, 
              buyers_premium_minimum: parseFloat(e.target.value) || 0 
            }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Currency *
        </label>
        <select
          value={formData.currency}
          onChange={e => setFormData(prev => ({ ...prev, currency: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        >
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="GBP">GBP</option>
          <option value="CAD">CAD</option>
          <option value="AUD">AUD</option>
          <option value="CHF">CHF</option>
          <option value="JPY">JPY</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Source URL
        </label>
        <input
          type="url"
          value={formData.source_url}
          onChange={e => setFormData(prev => ({ ...prev, source_url: e.target.value }))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          checked={formData.scraping_enabled}
          onChange={e => setFormData(prev => ({ ...prev, scraping_enabled: e.target.checked }))}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label className="ml-2 block text-sm text-gray-900">
          Enable automatic scraping
        </label>
      </div>

      {formData.scraping_enabled && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Scraping Frequency
          </label>
          <select
            value={formData.scraping_frequency}
            onChange={e => setFormData(prev => ({ ...prev, scraping_frequency: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="1 hours">Every hour</option>
            <option value="6 hours">Every 6 hours</option>
            <option value="12 hours">Every 12 hours</option>
            <option value="1 days">Every day</option>
            <option value="7 days">Every week</option>
          </select>
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          {auction ? 'Update' : 'Create'} Auction
        </button>
      </div>
    </form>
  );
}

// Main system auctions manager component
export function SystemAuctionsManager() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLotsModalOpen, setIsLotsModalOpen] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [selectedAuctionForLots, setSelectedAuctionForLots] = useState(null);
  const queryClient = useQueryClient();

  // Fetch system auctions
  const { data: auctions, isLoading: dataLoading, error: dataError } = useQuery({
    queryKey: ['admin', 'system-auctions'],
    queryFn: async () => {
      const { data } = await api.get('/admin/system-auctions');
      // Add user timezone to each auction
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return data.map(auction => ({
        ...auction,
        user_timezone: userTimezone
      }));
    }
  });

  // Create system auction
  const createMutation = useMutation({
    mutationFn: async (auction) => {
      try {
        const response = await api.post('/admin/system-auctions', auction);
        return response.data;
      } catch (error) {
        console.error('Full error response:', error.response);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin', 'system-auctions']);
      setIsModalOpen(false);
    },
    onError: (error) => {
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.errors?.map(err => err.msg).join('\n') || error.message;
      alert(errorMessage || 'Failed to create auction');
    }
  });

  // Update system auction
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...auction }) => {
      try {
        const response = await api.put(`/admin/system-auctions/${id}`, auction);
        return response.data;
      } catch (error) {
        console.error('Full error response:', error.response);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin', 'system-auctions']);
      setIsModalOpen(false);
      setSelectedAuction(null);
    },
    onError: (error) => {
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.errors?.map(err => err.msg).join('\n') || error.message;
      alert(errorMessage || 'Failed to update auction');
    }
  });

  // Delete system auction
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/admin/system-auctions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin', 'system-auctions']);
    }
  });

  // Update auction status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const response = await api.put(`/admin/system-auctions/${id}/status`, { status });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin', 'system-auctions']);
    }
  });

  const { preferences, loading: prefsLoading, error: prefsError } = usePreferences('system_auctions');
  
  const isLoading = dataLoading || prefsLoading;
  const error = dataError || prefsError;

  const columns = React.useMemo(() => [
    {
      headerName: 'Auction House',
      field: 'auction_house_name'
    },
    {
      headerName: 'Name',
      field: 'name'
    },
    {
      headerName: 'Start Date',
      field: 'date',
      renderCell: (item) => format(new Date(item.date).toISOString(), 'MMM d, yyyy')
    },
    {
      headerName: 'End Date',
      field: 'end_date',
      renderCell: (item) => item.end_date ? format(new Date(item.end_date).toISOString(), 'MMM d, yyyy') : '-'
    },
    {
      headerName: 'Source URL',
      field: 'source_url',
      renderCell: (item) => item.source_url ? (
        <a 
          href={item.source_url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          {item.source_url}
        </a>
      ) : '-'
    },
    {
      headerName: 'Currency',
      field: 'currency'
    },
    {
      headerName: 'Buyer\'s Premium',
      field: 'buyers_premium_percent',
      renderCell: (item) => `${item.buyers_premium_percent}%`
    },
    {
      headerName: 'Premium Min',
      field: 'buyers_premium_minimum',
      renderCell: (item) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: item.currency
        }).format(item.buyers_premium_minimum);
      }
    },
    {
      headerName: 'Status',
      field: 'status',
      renderCell: (item) => {
        const statusColors = {
          pending: "bg-amber-100 hover:bg-amber-200",
          active: "bg-green-100 hover:bg-green-200",
          completed: "bg-blue-100 hover:bg-blue-200",
          cancelled: "bg-red-100 hover:bg-red-200",
          hidden: "bg-gray-100 hover:bg-gray-200"
        };

        return (
          <select
            value={item.status}
            onChange={(e) => {
              e.preventDefault();
              e.stopPropagation();
              updateStatusMutation.mutate({
                id: item.id,
                status: e.target.value
              });
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${statusColors[item.status]}`}
          >
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="hidden">Hidden</option>
          </select>
        );
      }
    },
    {
      headerName: 'Daily Times',
      field: 'daily_times_original',
      renderCell: (item) => (
        <div className="whitespace-pre-wrap">
          {Object.entries(item.daily_times).map(([date, time]) => (
            `${date.split('-').slice(1).concat(date.split('-')[0]).join('/')}: ${time}\n`
          )).join('')}
        </div>
      )
    },
    {
      headerName: 'Daily Times (Your Time)',
      field: 'daily_times',
      renderCell: (item) => {
        // Get user timezone from the auction object (added in queryFn)
        const userTimezone = item.user_timezone;
        return (
          <div className="whitespace-pre-wrap">
            {Object.entries(item.daily_times).map(([date, time]) => {
              const [hours, minutes] = time.split(':');
              
              // Create date in auction timezone and convert to user timezone
              const auctionDateTime = dayjs.tz(
                `${date} ${hours}:${minutes}`,
                'YYYY-MM-DD HH:mm',
                item.timezone || 'UTC'
              );
              
              if (!auctionDateTime.isValid()) {
                return `${date.split('-').slice(1).concat(date.split('-')[0]).join('/')}: Invalid time\n`;
              }
              
              const userDateTime = auctionDateTime.tz(userTimezone);
              return `${userDateTime.format('MM/DD/YYYY')}: ${userDateTime.format('HH:mm')}\n`;
            }).join('')}
          </div>
        );
      },
      sortValue: (item) => {
        // Get first date/time for sorting
        const userTimezone = item.user_timezone;
        const times = Object.entries(item.daily_times);
        if (times.length === 0) return '';
        
        const [date, time] = times[0];
        const [hours, minutes] = time.split(':');
        
        // Create date in auction timezone and convert to user timezone
        const auctionDateTime = dayjs.tz(
          `${date} ${hours}:${minutes}`,
          'YYYY-MM-DD HH:mm',
          item.timezone || 'UTC'
        );
        
        if (!auctionDateTime.isValid()) return '';
        
        const userDateTime = auctionDateTime.tz(userTimezone);
        return userDateTime.format('YYYY-MM-DD HH:mm'); // Format for proper string sorting
      }
    },
    {
      headerName: 'Last Scraped',
      field: 'last_scraped_at',
      renderCell: (item) => item.last_scraped_at ? format(new Date(item.last_scraped_at).toISOString(), 'MMM d, yyyy h:mm a') : '-'
    },
    {
      headerName: 'Created',
      field: 'created_at',
      renderCell: (item) => format(new Date(item.created_at).toISOString(), 'MMM d, yyyy h:mm a')
    },
    {
      headerName: 'Updated',
      field: 'updated_at',
      renderCell: (item) => format(new Date(item.updated_at).toISOString(), 'MMM d, yyyy h:mm a')
    },
    {
      headerName: 'Lots',
      field: 'lot_count'
    },
    {
      headerName: 'Timezone',
      field: 'timezone',
      renderCell: (item) => {
        const timezoneLabels = {
          'America/New_York': 'Eastern Time',
          'America/Chicago': 'Central Time',
          'America/Denver': 'Mountain Time',
          'America/Los_Angeles': 'Pacific Time',
          'America/Anchorage': 'Alaska Time',
          'Pacific/Honolulu': 'Hawaii Time',
          'UTC': 'UTC',
          'Europe/London': 'London',
          'Europe/Paris': 'Paris',
          'Europe/Berlin': 'Berlin'
        };
        return timezoneLabels[item.timezone] || item.timezone;
      }
    },
    {
      headerName: 'Auto Scraping',
      field: 'scraping_enabled',
      renderCell: (item) => item.scraping_enabled ? 'Yes' : 'No'
    },
    {
      headerName: 'Scraping Frequency',
      field: 'scraping_frequency',
      renderCell: (item) => {
        if (!item.scraping_enabled) return '-';
        // Handle case where scraping_frequency might be an object or string
        if (typeof item.scraping_frequency === 'object' && item.scraping_frequency !== null) {
          const { amount = 1, unit = 'days' } = item.scraping_frequency;
          return `${amount} ${unit}`;
        }
        return item.scraping_frequency || '1 days';
      }
    },
    {
      headerName: 'Actions',
      field: 'actions',
      renderCell: (item) => (
        <div className="flex space-x-2 pointer-events-auto">
          <button
            className="px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e._handled = true;
              setSelectedAuction(item);
              setIsModalOpen(true);
            }}
            type="button"
          >
            Edit
          </button>
          <button
            className="px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e._handled = true;
              setSelectedAuctionForLots(item);
              setIsLotsModalOpen(true);
            }}
            type="button"
          >
            Manage Lots
          </button>
          <button
            className="px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e._handled = true;
              if (window.confirm('Are you sure you want to delete this auction?')) {
                deleteMutation.mutate(item.id);
              }
            }}
            type="button"
          >
            Delete
          </button>
        </div>
      )
    }
  ], [deleteMutation]);

  return (
    <div>
      <div className="bg-white rounded-lg shadow flex-1 min-h-0 overflow-hidden">
        <TableProvider
          type="system_auctions"
          initialState={{
            loading: isLoading,
            error: error,
            preferences: preferences || {
              density: 'standard',
              columnWidths: {
                auction_house_name: 150,
                name: 200,
                date: 120,
                end_date: 120,
                source_url: 200,
                currency: 100,
                buyers_premium_percent: 150,
                buyers_premium_minimum: 150,
                lot_count: 100,
                status: 100,
                daily_times: 200,
                daily_times_local: 200,
                last_scraped_at: 150,
                created_at: 150,
                updated_at: 150,
                timezone: 150,
                scraping_enabled: 120,
                scraping_frequency: 150,
                actions: 300
              }
            }
          }}
        >
          <SystemAuctionsContent
            columns={columns}
            data={auctions || []}
            onAdd={() => {
              setSelectedAuction(null);
              setIsModalOpen(true);
            }}
            loading={isLoading}
            error={error}
          />
        </TableProvider>
      </div>

      {/* Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => {
              setIsModalOpen(false);
              setSelectedAuction(null);
            }}></div>
            <div className="relative bg-white rounded-lg max-w-lg w-full">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-medium">
                  {selectedAuction ? 'Edit System Auction' : 'Add System Auction'}
                </h3>
              </div>
              <div className="p-6">
                <SystemAuctionForm
                  auction={selectedAuction}
                  onSubmit={(formData) => {
                    if (selectedAuction) {
                      updateMutation.mutate({ id: selectedAuction.id, ...formData });
                    } else {
                      createMutation.mutate(formData);
                    }
                  }}
                  onCancel={() => {
                    setIsModalOpen(false);
                    setSelectedAuction(null);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lots Management Modal */}
      {isLotsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => {
              setIsLotsModalOpen(false);
              setSelectedAuctionForLots(null);
            }}></div>
            <div className="relative bg-white rounded-lg w-[95vw]">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-medium">
                  Manage Lots - {selectedAuctionForLots?.name}
                </h3>
              </div>
              <div className="p-6">
                {selectedAuctionForLots && (
                  <SystemAuctionLots auctionId={selectedAuctionForLots.id} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
